import {
  Level,
  CharacterTrait,
  ExperienceEntry,
  AdvancementChoice,
  TierAdvancementState,
  LevelUpSession,
  MarkedTraits,
  getUnmarkedTraits,
  hasAvailableSlot,
  hasNAvailableSlots,
  getAdvancementSlotCost,
  isTierAchievementLevel,
  isMulticlassAvailable
} from '../types/levelUp';
import { CharacterData } from '../types/character';

// ============================================================================
// VALIDATION RESULT TYPE
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

// ============================================================================
// RULE 1: LEVEL PROGRESSION CONSTRAINTS
// ============================================================================

/**
 * Validates that a level-up is allowed for the current character
 */
export function validateLevelProgression(character: CharacterData): ValidationResult {
  const errors: string[] = [];
  const currentLevel = parseInt(character.level);

  // Cannot level up if already at max level
  if (currentLevel >= 10) {
    errors.push('Character is already at maximum level (10)');
  }

  // Character must have a valid level (1-9 to level up)
  if (currentLevel < 1 || currentLevel > 9) {
    errors.push(`Invalid character level: ${currentLevel}`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Gets the next valid level for a character
 */
export function getNextLevel(currentLevel: number): Level | null {
  if (currentLevel >= 10) return null;
  if (currentLevel < 1) return null;
  return (currentLevel + 1) as Level;
}

/**
 * Checks if a character can level up
 */
export function canCharacterLevelUp(character: CharacterData): boolean {
  const currentLevel = parseInt(character.level);
  return currentLevel >= 1 && currentLevel < 10;
}

// ============================================================================
// RULE 2: TIER ACHIEVEMENT REQUIREMENTS
// ============================================================================

/**
 * Validates tier achievement input
 */
export function validateTierAchievement(
  level: Level,
  experienceName: string,
  existingExperiences: ExperienceEntry[]
): ValidationResult {
  const errors: string[] = [];

  // Verify this level has tier achievements
  if (!isTierAchievementLevel(level)) {
    errors.push(`Level ${level} does not have tier achievements`);
  }

  // Experience name is required
  const trimmedName = experienceName.trim();
  if (!trimmedName) {
    errors.push('Please enter a name for your new experience');
  }

  // Experience name length validation
  if (trimmedName.length > 0 && trimmedName.length < 3) {
    errors.push('Experience name must be at least 3 characters');
  }

  if (trimmedName.length > 50) {
    errors.push('Experience name must be at most 50 characters');
  }

  // Experience name must be unique (case-insensitive)
  const isDuplicate = existingExperiences.some(exp =>
    exp.name.toLowerCase() === trimmedName.toLowerCase()
  );

  if (isDuplicate) {
    errors.push(`You already have an experience called "${trimmedName}"`);
  }

  // Validate characters (alphanumeric, spaces, hyphens, apostrophes only)
  const validNamePattern = /^[a-zA-Z0-9\s\-']+$/;
  if (trimmedName.length > 0 && !validNamePattern.test(trimmedName)) {
    errors.push('Experience name can only contain letters, numbers, spaces, hyphens, and apostrophes');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// RULE 3: ADVANCEMENT SLOT AVAILABILITY
// ============================================================================

/**
 * Validates advancement slot availability
 */
export function validateAdvancementSlots(
  choice: AdvancementChoice,
  tierState: TierAdvancementState,
  remainingSlots: number
): ValidationResult {
  const errors: string[] = [];
  const cost = getAdvancementSlotCost(choice);

  // Check if enough advancement slots remain
  if (cost > remainingSlots) {
    errors.push(`This advancement requires ${cost} slot(s), but only ${remainingSlots} remain`);
  }

  // Check advancement type specific slots
  switch (choice.type) {
    case 'characterTraits':
      if (!hasAvailableSlot(tierState.slots.characterTraits)) {
        errors.push('No character trait advancement slots available in this tier');
      }
      break;

    case 'hitPoints':
      if (!hasAvailableSlot(tierState.slots.hitPoints)) {
        errors.push('No hit point advancement slots available in this tier');
      }
      break;

    case 'stress':
      if (!hasAvailableSlot(tierState.slots.stress)) {
        errors.push('No stress advancement slots available in this tier');
      }
      break;

    case 'experience':
      if (!hasAvailableSlot(tierState.slots.experiences)) {
        errors.push('No experience advancement slots available in this tier');
      }
      break;

    case 'domainCard':
      if (!hasAvailableSlot(tierState.slots.domainCard)) {
        errors.push('No domain card advancement slots available in this tier');
      }
      break;

    case 'evasion':
      if (!hasAvailableSlot(tierState.slots.evasion)) {
        errors.push('No evasion advancement slots available in this tier');
      }
      break;

    case 'subclassUpgrade':
      if (!hasAvailableSlot(tierState.slots.subclassUpgrade)) {
        errors.push('No subclass upgrade advancement slots available in this tier');
      }
      if (tierState.crossedOut.subclassUpgrade) {
        errors.push('Subclass upgrade is disabled (multiclass taken in this tier)');
      }
      break;

    case 'proficiency':
      if (!hasNAvailableSlots(tierState.slots.proficiency, 2)) {
        errors.push('Proficiency advancement requires 2 slots in this tier');
      }
      break;

    case 'multiclass':
      if (!hasNAvailableSlots(tierState.slots.multiclass, 2)) {
        errors.push('Multiclass advancement requires 2 slots in this tier');
      }
      if (tierState.crossedOut.multiclass) {
        errors.push('Multiclass is disabled (subclass upgrade taken in this tier)');
      }
      break;
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Checks if an advancement option should be disabled in the UI
 */
export function isAdvancementDisabled(
  advancementType: keyof TierAdvancementState['slots'],
  tierState: TierAdvancementState,
  remainingSlots: number
): boolean {
  const slotCost = advancementType === 'proficiency' || advancementType === 'multiclass' ? 2 : 1;

  // Not enough general advancement slots
  if (slotCost > remainingSlots) return true;

  // Check tier-specific slots
  const tierSlots = tierState.slots[advancementType];
  if (!hasNAvailableSlots(tierSlots, slotCost)) return true;

  // Check mutual exclusions
  if (advancementType === 'subclassUpgrade' && tierState.crossedOut.subclassUpgrade) {
    return true;
  }
  if (advancementType === 'multiclass' && tierState.crossedOut.multiclass) {
    return true;
  }

  return false;
}

// ============================================================================
// RULE 4: CHARACTER TRAIT MARKING SYSTEM
// ============================================================================

/**
 * Validates character trait selection
 */
export function validateCharacterTraits(
  trait1: CharacterTrait,
  trait2: CharacterTrait,
  markedTraits: MarkedTraits
): ValidationResult {
  const errors: string[] = [];

  // Traits must be different
  if (trait1 === trait2) {
    errors.push('You must select two different traits');
  }

  // Get unmarked traits
  const unmarkedTraits = getUnmarkedTraits(markedTraits);

  // Check if trait1 is available
  if (!unmarkedTraits.includes(trait1)) {
    errors.push(`${trait1} is already marked and cannot be increased again this tier`);
  }

  // Check if trait2 is available
  if (!unmarkedTraits.includes(trait2)) {
    errors.push(`${trait2} is already marked and cannot be increased again this tier`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Gets list of traits that can be selected
 */
export function getSelectableTraits(markedTraits: MarkedTraits): CharacterTrait[] {
  return getUnmarkedTraits(markedTraits);
}

// ============================================================================
// RULE 5: EXPERIENCE ADVANCEMENT VALIDATION
// ============================================================================

/**
 * Validates experience advancement choice
 */
export function validateExperienceAdvancement(
  experienceId: string,
  experiences: ExperienceEntry[]
): ValidationResult {
  const errors: string[] = [];

  // Experience must exist
  const experience = experiences.find(e => e.id === experienceId);
  if (!experience) {
    errors.push('Selected experience not found');
  }

  // Optional: Warn if experience is already very high
  if (experience && experience.bonus >= 5) {
    return {
      valid: true,
      errors: [],
      warnings: [`This experience is already at +${experience.bonus}. Consider diversifying your experiences.`]
    };
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// RULE 6: DOMAIN CARD VALIDATION
// ============================================================================

/**
 * Validates domain card level against character level
 */
export function validateDomainCardLevel(
  cardLevel: number,
  characterLevel: Level,
  isMulticlassDomain: boolean = false
): ValidationResult {
  const errors: string[] = [];

  const maxLevel = isMulticlassDomain
    ? Math.ceil(characterLevel / 2)
    : characterLevel;

  if (cardLevel > maxLevel) {
    const context = isMulticlassDomain ? ' for multiclass domain' : '';
    errors.push(`Card level ${cardLevel} exceeds maximum ${maxLevel}${context}`);
  }

  if (cardLevel < 1) {
    errors.push('Card level must be at least 1');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates domain card exchange
 */
export function validateDomainCardExchange(
  oldCardId: string,
  newCardId: string,
  existingCards: string[]
): ValidationResult {
  const errors: string[] = [];

  // Old card must exist in current loadout/vault
  if (!existingCards.includes(oldCardId)) {
    errors.push('Selected card to exchange is not in your collection');
  }

  // New card must be different from old card
  if (oldCardId === newCardId) {
    errors.push('New card must be different from the card being exchanged');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// RULE 7: MULTICLASS VALIDATION
// ============================================================================

/**
 * Validates multiclass advancement
 */
export function validateMulticlass(
  character: CharacterData,
  newClassId: string
): ValidationResult {
  const errors: string[] = [];
  const currentLevel = parseInt(character.level);

  // Multiclass only available at level 5+
  if (!isMulticlassAvailable(currentLevel as Level)) {
    errors.push('Multiclass is only available starting at level 5');
  }

  // Cannot multiclass into same class
  if (character.class === newClassId) {
    errors.push('Cannot multiclass into your current class');
  }

  // Cannot multiclass if already multiclassed (Phase 26A limitation)
  if (character.multiclass) {
    errors.push('Character already has a multiclass (only one multiclass allowed)');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates multiclass eligibility based on tier advancement state
 */
export function validateMulticlassEligibility(
  character: CharacterData,
  tierState: TierAdvancementState
): ValidationResult {
  const errors: string[] = [];
  const currentLevel = parseInt(character.level);

  // Level requirement
  if (currentLevel < 5) {
    errors.push('Multiclass is only available at level 5 or higher');
  }

  // Can only multiclass once
  if (character.multiclass) {
    errors.push('You have already multiclassed - only one multiclass allowed');
  }

  // Check tier slots (requires 2 slots)
  if (!hasNAvailableSlots(tierState.slots.multiclass, 2)) {
    errors.push('Multiclass requires 2 advancement slots in this tier');
  }

  // Check mutual exclusion (subclass upgrade taken in this tier)
  if (tierState.crossedOut.multiclass) {
    errors.push('Multiclass disabled - subclass upgrade was taken in this tier');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates multiclass class selection
 */
export function validateMulticlassClassChoice(
  classId: string,
  character: CharacterData
): ValidationResult {
  const errors: string[] = [];

  // Cannot multiclass into same class
  if (classId.toLowerCase() === character.class.toLowerCase()) {
    errors.push('Cannot multiclass into your current class');
  }

  // Class must be valid (basic check)
  if (!classId || classId.trim() === '') {
    errors.push('You must select a class');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates domain selection for multiclass
 */
export function validateMulticlassDomain(
  domainId: string,
  selectedClassDomains: string[]
): ValidationResult {
  const errors: string[] = [];

  // Domain must be from the selected class
  if (!selectedClassDomains.some(d => d.toLowerCase() === domainId.toLowerCase())) {
    errors.push('Selected domain is not available for this class');
  }

  // Domain must be selected
  if (!domainId || domainId.trim() === '') {
    errors.push('You must select a domain');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validates foundation card selection for multiclass
 */
export function validateMulticlassFoundationCard(
  foundationCardId: string,
  availableFoundationCards: Array<{ cardId: string }>
): ValidationResult {
  const errors: string[] = [];

  // Foundation card must be from available options
  if (!availableFoundationCards.some(fc => fc.cardId === foundationCardId)) {
    errors.push('Selected foundation card is not available for this subclass');
  }

  // Foundation card must be selected
  if (!foundationCardId || foundationCardId.trim() === '') {
    errors.push('You must select a foundation card');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// RULE 8: SESSION COMPLETION VALIDATION
// ============================================================================

/**
 * Validates that a level-up session is complete and ready to commit
 */
export function validateLevelUpSessionCompletion(
  session: LevelUpSession
): ValidationResult {
  const errors: string[] = [];

  // Step 1: Tier Achievement (if applicable)
  if (session.tierAchievement) {
    if (!session.tierAchievement.newExperience) {
      errors.push('You must create a new experience for the tier achievement');
    }
  }

  // Step 2: Advancement Choices (must use exactly 2 slots)
  const slotsUsed = session.advancementChoices.reduce(
    (total, choice) => total + getAdvancementSlotCost(choice),
    0
  );

  if (slotsUsed === 0) {
    errors.push('You must select advancements (2 slots worth)');
  } else if (slotsUsed < 2) {
    errors.push(`You have used ${slotsUsed} advancement slot(s). You must use exactly 2 slots.`);
  } else if (slotsUsed > 2) {
    errors.push(`You have used ${slotsUsed} advancement slot(s). You can only use 2 slots per level.`);
  }

  // Step 3: Damage Thresholds (automatic, just verify)
  if (!session.damageThresholdsIncreased) {
    errors.push('Damage thresholds must be increased (+1 to each)');
  }

  // Step 4: Domain Cards (required - at least 1, or 2 if additional domain card selected)
  const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
  if (!session.newDomainCards || session.newDomainCards.length !== requiredCardCount) {
    errors.push(`You must select ${requiredCardCount} domain card${requiredCardCount > 1 ? 's' : ''}`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Gets progress summary for level-up session
 */
export function getLevelUpProgress(session: LevelUpSession): {
  step1Complete: boolean;
  step2Complete: boolean;
  step3Complete: boolean;
  step4Complete: boolean;
  overallComplete: boolean;
} {
  const step1Complete = !session.tierAchievement || !!session.tierAchievement.newExperience;

  const slotsUsed = session.advancementChoices.reduce(
    (total, choice) => total + getAdvancementSlotCost(choice),
    0
  );
  const step2Complete = slotsUsed === 2;

  const step3Complete = session.damageThresholdsIncreased;

  // Check if required number of domain cards selected
  const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
  const step4Complete = session.newDomainCards && session.newDomainCards.length === requiredCardCount;

  return {
    step1Complete,
    step2Complete,
    step3Complete,
    step4Complete,
    overallComplete: step1Complete && step2Complete && step3Complete && step4Complete
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Gets human-readable slot status text
 */
export function getSlotStatusText(slots: boolean[]): string {
  const total = slots.length;
  const used = slots.filter(s => s).length;
  const available = total - used;

  return `${used}/${total} slots used (${available} available)`;
}

/**
 * Combines multiple validation results
 */
export function combineValidationResults(
  ...results: ValidationResult[]
): ValidationResult {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  for (const result of results) {
    allErrors.push(...result.errors);
    if (result.warnings) {
      allWarnings.push(...result.warnings);
    }
  }

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings.length > 0 ? allWarnings : undefined
  };
}

/**
 * Creates a successful validation result
 */
export function validationSuccess(warnings?: string[]): ValidationResult {
  return {
    valid: true,
    errors: [],
    warnings
  };
}

/**
 * Creates a failed validation result
 */
export function validationFailure(errors: string | string[]): ValidationResult {
  return {
    valid: false,
    errors: Array.isArray(errors) ? errors : [errors]
  };
}
